import { useEffect, useState } from 'react';

/**
 * @typedef {{ text: string; repeat: boolean }} QueueItem
 * @typedef {QueueItem | string} QueueEntry
 */

export default function useTextToSpeech(initVoice = 0) {
  /** @type {[SpeechSynthesisVoice[] | undefined, React.Dispatch<React.SetStateAction<SpeechSynthesisVoice[] | undefined>>]} */
  const [voices, setVoices] = useState();
  const [selectedVoice, setSelectedVoice] = useState(initVoice);
  const [currentIndex, setCurrentIndex] = useState(0); // Track current utterance index

  useEffect(() => {
    window.speechSynthesis.cancel();
    const fetchVoices = () => {
      const voicesList = window.speechSynthesis.getVoices();
      setVoices(voicesList);
    };

    // Fetch voices initially
    fetchVoices();

    // Fetch voices when they change (e.g., on different devices)
    window.speechSynthesis.onvoiceschanged = fetchVoices;
    return () => {
      // Clean up event handler on unmount
      if (window.speechSynthesis.onvoiceschanged === fetchVoices) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const isSpeaking = () => window.speechSynthesis.speaking;
  const isPending = () => window.speechSynthesis.pending;

  /**
   * @param {string} text
   * @param {number} [volume]
   * @returns {void}
   */
  const speak = (text, volume = 1.0) => {
    const safeText = text === undefined || text === null ? '' : String(text);
    if (!safeText.trim()) {
      return;
    }

    const utterance = new SpeechSynthesisUtterance(safeText);
    utterance.volume = Math.max(0.3, Math.min(1.0, volume));

    // Suppress repeated error logs to avoid console spam
    utterance.addEventListener('error', () => {
      // Log Errors here
    });

    if (voices && voices[selectedVoice]) {
      utterance.voice = voices[selectedVoice];
    }

    try {
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.warn('Speech synthesis speak failed:', error);
    }
  };

  // Unified: always process the queue the same way, starting from startIndex
  // Accepts getVolume as a function to always get the latest value
  /**
   * @param {QueueEntry[]} newQueue
   * @param {boolean} repeat
   * @param {boolean} interrupt
   * @param {number} [volume]
   * @param {number} [startIndex]
   * @param {(() => number) | undefined} [getVolume]
   * @returns {void}
   */
  const beginNewQueue = (newQueue, repeat, interrupt, volume = 1.0, startIndex = 0, getVolume) => {
    if (!Array.isArray(newQueue) || newQueue.length === 0) return;
    setCurrentIndex(startIndex);
    // Always cancel before starting new queue
    window.speechSynthesis.cancel();
    const queue = newQueue.slice(startIndex);
    queue.forEach((item, index) => {
      const utterIdx = startIndex + index;
      const safeText = item.text !== undefined ? item.text : item;
      // Use getVolume if provided, else fallback to passed volume
      const currentVolume = typeof getVolume === 'function' ? getVolume() : volume;
      const utterance = new SpeechSynthesisUtterance(safeText);
      utterance.volume = Math.max(0.3, Math.min(1.0, currentVolume));
      if (voices && voices[selectedVoice]) utterance.voice = voices[selectedVoice];
      utterance.onstart = () => {
        setCurrentIndex(utterIdx);
      };
      utterance.onend = () => {
        setCurrentIndex(utterIdx + 1);
        // If repeat is enabled and this is the last utterance, restart the queue with latest volume
        if (repeat && index === queue.length - 1) {
          beginNewQueue(newQueue, repeat, interrupt, currentVolume, 0, getVolume);
        }
      };
      window.speechSynthesis.speak(utterance);
    });
  };

  return {
    voices,
    isSpeaking,
    isPending,
    /** @param {number} index */
    setSelectedVoice,
    cancel: () => window.speechSynthesis.cancel(),
    /**
     * @param {QueueEntry[]} text
     * @param {boolean} [repeat]
     * @param {boolean} [interrupt]
     * @param {number} [volume]
     * @param {number} [startIndex]
     * @param {(() => number) | undefined} [getVolume]
     */
    beginNewQueue: (
      text,
      repeat = false,
      interrupt = false,
      volume = 1.0,
      startIndex = 0,
      getVolume,
    ) => beginNewQueue(text, repeat, interrupt, volume, startIndex, getVolume),
    /** @param {string} text @param {number} [volume] */
    speak,
    getCurrentIndex: () => currentIndex,
  };
}
